import { useEffect, useState, useCallback, useRef } from "react";
import { fetchFastApi } from "../api/fastapi";
import { EventRun, EventRunSchema } from "../models/audit/eventRun";
import { parseModelArrayTolerant } from "../lib/parseModelArray";
import { z } from "zod";

// Envelope must be shaped right (a real transport failure should throw into
// the error path), but rows parse per-element so one malformed run degrades
// only itself, not this whole polling surface (MS-3366).
const EventRunsResponseSchema = z.object({
  eventRuns: z.array(z.unknown()),
  totalCount: z.number(),
});

interface UseBackgroundTasksOptions {
  limit?: number;
  userId?: string;
  refreshInterval?: number; // Auto refresh every N milliseconds
  enabled?: boolean; // Only fetch if enabled is true
}

export const useBackgroundTasks = ({
  limit = 50,
  userId,
  refreshInterval,
  enabled = true,
}: UseBackgroundTasksOptions = {}) => {
  const [eventRuns, setEventRuns] = useState<EventRun[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const offsetRef = useRef<number>(0);
  const isLoadingRef = useRef<boolean>(false);
  const userIdRef = useRef<string | undefined>(userId);
  userIdRef.current = userId;
  // Monotonic id of the newest fetch. A reset fetch (poll, manual refresh,
  // post-stop refetch) always goes out and claims the newest id; older
  // in-flight requests see they've been superseded and commit nothing, so a
  // poll issued before a stop can't land afterwards and repaint the stopped
  // card as still running. Page-appends (`loadMore`) stay serialized behind
  // `isLoadingRef` since they must arrive in order.
  const requestIdRef = useRef<number>(0);

  const fetchEventRuns = useCallback(
    async (reset: boolean = false): Promise<void> => {
      if (!enabled) return;
      if (!reset && isLoadingRef.current) return;

      const requestId = ++requestIdRef.current;
      const isCurrent = () => requestId === requestIdRef.current;
      isLoadingRef.current = true;
      const currentOffset = reset ? 0 : offsetRef.current;
      const currentUserId = userIdRef.current;

      try {
        if (reset) {
          setIsLoading(true);
        } else {
          setIsLoadingMore(true);
        }
        setError(null);

        const query: Record<string, string> = {
          limit: limit.toString(),
          offset: currentOffset.toString(),
        };

        if (currentUserId) {
          query.userId = currentUserId;
        }

        const response = await fetchFastApi({
          method: "GET",
          path: "/notifications/event-runs",
          query,
        });

        const validatedResponse = EventRunsResponseSchema.parse(response);
        const parsedEventRuns = parseModelArrayTolerant(
          EventRunSchema,
          validatedResponse.eventRuns,
          "event run",
        );
        // A newer request owns the state now; this one's data is stale.
        if (!isCurrent()) return;
        setTotalCount(validatedResponse.totalCount);

        if (reset) {
          setEventRuns(parsedEventRuns);
        } else {
          setEventRuns((prev) => [
            ...prev,
            ...parsedEventRuns.filter(
              (run) => !prev.some((p) => p.id === run.id),
            ),
          ]);
        }

        // Determine if more data is available — off the RAW row count, so a
        // dropped malformed row can't end pagination early.
        setHasMore(validatedResponse.eventRuns.length === limit);

        // Update offset for the next fetch
        const newOffset = reset ? limit : currentOffset + limit;
        offsetRef.current = newOffset;
      } catch (err) {
        // A superseded request's failure is as stale as its data would be.
        if (!isCurrent()) return;
        console.error("Error fetching event runs:", err);
        setError(
          err instanceof Error ? err : new Error("Failed to fetch event runs"),
        );
      } finally {
        // The newest request owns the loading flags; a superseded one leaves
        // them to whichever call superseded it.
        if (isCurrent()) {
          setIsLoading(false);
          setIsLoadingMore(false);
          isLoadingRef.current = false;
        }
      }
    },
    [limit, enabled],
  );

  const loadMore = useCallback(() => {
    if (isLoadingRef.current || !hasMore) return;
    fetchEventRuns(false);
  }, [hasMore, fetchEventRuns]);

  // Resolves once the fresh first page has landed (or been superseded), so a
  // caller that `await`s it can act on up-to-date cards. The current cards
  // stay on screen until that page replaces them, so a refresh doesn't
  // blank the list and flicker.
  const refetch = useCallback((): Promise<void> => {
    offsetRef.current = 0;
    setHasMore(true);
    return fetchEventRuns(true);
  }, [fetchEventRuns]);

  useEffect(() => {
    if (!enabled) return;

    // Reset and fetch initial data when userId changes
    offsetRef.current = 0;
    setHasMore(true);
    setEventRuns([]);
    fetchEventRuns(true);

    // Set up auto-refresh if refreshInterval is provided
    if (refreshInterval && refreshInterval > 0) {
      const intervalId = setInterval(() => {
        // Only refresh the first page to avoid duplicates
        offsetRef.current = 0;
        fetchEventRuns(true);
      }, refreshInterval);

      return () => clearInterval(intervalId);
    }
  }, [userId, fetchEventRuns, refreshInterval, enabled]);

  return {
    eventRuns,
    totalCount,
    isLoading,
    isLoadingMore,
    hasMore,
    error,
    refetch,
    loadMore,
  };
};
